//@flow
import React, {useState} from 'react';
import {Link} from 'react-router-dom';
import {
  makeStyles,
  Typography,
  Grid,
  Box,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
} from '@material-ui/core';
import {colors} from '../../../assets/styles/colors';
import {Delete, Settings} from '@material-ui/icons';
import {useIsFeatureInTier} from '../../../utils/component';
import CustomButton from '../../../elements/CustomButton/CustomButton';
import {useChatBots, useChatBotsActions} from '../hooks';
import UpgradeIcon from '../../../elements/icons/UpgradeIcon';
import IosSwitch from '../../../elements/IosSwitch/IosSwitch';
import CustomChip from '../../../elements/CustomChip';
import {getChatBotStateLabel, getChatBotStateColor} from '../utils';
import BotCreationModal from './BotCreationModal';
import {useAlert} from '../../../elements/Alert/AlertContext';
import ConfirmModal from '../../../elements/Modals/ConfirmModal';
import InlineLoader from '../../../elements/LoadingSpinner/InlineLoader';

const useStyles = makeStyles(theme => ({
  root: {
    marginBottom: '2rem',
  },
  header: {
    fontSize: '1.5rem',
    fontWeight: 700,
    marginTop: '1rem',
    color: colors.grey900,
  },
  container: {
    paddingInline: '1rem',
  },
  sectionCard: {
    padding: '.75rem',
    [theme.breakpoints.down('xs')]: {
      padding: '1rem',
    },
    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.05)',
    backgroundColor: 'white',
    borderRadius: '16px',
    overflow: 'hidden',
    border: `1px solid ${colors.grey200}`,
  },
  subtitle: {
    fontSize: '1.125rem',
    fontWeight: 600,
    color: colors.grey900,
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '.5rem',
  },
  uploadArea: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    aspectRatio: '3/2',
    background: colors.grey50,
    borderRadius: '12px',
    border: `1px dashed ${colors.grey300}`,
    maxHeight: '200px',
    gap: '.5rem',
    flexDirection: 'column',
    color: colors.primary,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    '&:hover': {
      background: colors.grey100,
      borderColor: colors.primary,
    },
  },
  caption: {
    fontSize: '.875rem',
    color: colors.grey500,
  },
  deleteButton: {
    color: colors.danger,
    marginLeft: theme.spacing(1),
    '&:hover': {
      backgroundColor: 'rgba(244, 67, 54, 0.08)',
    },
  },
  sectionFooter: {
    display: 'flex',
    justifyContent: 'flex-start',
    marginTop: '1.5rem',
  },
  submitButton: {
    maxWidth: '200px',
  },
  emptyContent: {
    minHeight: '300px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
    gap: '1rem',
  },
  upgradeButton: {
    backgroundColor: 'white',
    color: colors.purple3,
    fontWeight: 600,
    border: `1px solid ${colors.grey200}`,
    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.05)',
    '&:hover': {
      backgroundColor: colors.grey50,
    },
  },
  upgradeButtonLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '.5rem',
    justifyContent: 'center',
  },
  tableHead: {
    backgroundColor: colors.grey50,
    '& th': {
      fontWeight: 600,
      color: colors.grey700,
      borderBottom: `1px solid ${colors.grey200}`,
    },
  },
  tableRow: {
    '&:last-child td': {
      borderBottom: 0,
    },
    transition: 'background-color 0.2s ease',
    '&:hover': {
      backgroundColor: colors.grey50,
    },
  },
  botName: {
    fontSize: '0.9375rem',
    fontWeight: 600,
    color: colors.grey900,
  },
  activeText: {
    fontSize: '0.75rem',
    fontWeight: 600,
    color: colors.success,
    marginTop: 2,
  },
}));

export default function StoreBot() {
  const classes = useStyles();

  const [isLoading, setIsLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [botToDelete, setBotToDelete] = useState(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const {chatBots, refresh} = useChatBots();
  const {
    createChatBot,
    deleteChatBot,
    toggleEnableChatBot,
  } = useChatBotsActions();
  const {showAlert} = useAlert();

  const hasStoreAiChatBotFeature = useIsFeatureInTier('TIENDACHAT_AI_CHATBOT');

  // Polling for bots in TRAINING status
  React.useEffect(() => {
    const hasTrainingBots = chatBots.some(bot => bot.state === 'TRAINING');

    if (hasTrainingBots) {
      const interval = setInterval(() => {
        refresh();
      }, 10000); // Poll every 10 seconds

      return () => clearInterval(interval);
    }
  }, [chatBots, refresh]);

  const handleDeleteBot = bot => {
    setBotToDelete(bot);
    setConfirmDeleteOpen(true);
  };

  const confirmDelete = () => {
    if (!botToDelete) return;

    deleteChatBot(botToDelete.id)
      .then(success => {
        if (success) {
          showAlert('Bot eliminado exitosamente', {type: 'success'});
          refresh();
        } else {
          showAlert('Error al eliminar el bot', {type: 'error'});
        }
      })
      .finally(() => {
        setConfirmDeleteOpen(false);
        setBotToDelete(null);
      });
  };

  const handleCreateBot = (payload: {name: string}) => {
    setIsLoading(true);

    createChatBot(payload.name, '')
      .then(success => {
        if (success) {
          showAlert('Bot creado exitosamente', {type: 'success'});
          setOpenModal(false);
          refresh();
        } else {
          showAlert('No se pudo crear el bot. Por favor, intenta nuevamente.', {
            type: 'error',
          });
        }
      })
      .catch(() => {
        showAlert(
          'Ocurrió un error al crear el bot. Por favor, intenta nuevamente.',
          {type: 'error'}
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const activeChatBots = chatBots.filter(chatBot => !chatBot.archived) || [];

  const canCreateNewBot = activeChatBots.length !== 0;

  return (
    <div className={classes.root}>
      <Grid container spacing={3} className={classes.container}>
        <Grid item xs={12}>
          <div className={classes.header}>Bots de la tienda</div>
        </Grid>

        {!hasStoreAiChatBotFeature && (
          <Grid item xs={12}>
            <CustomButton className={classes.upgradeButton} to="/tiers-list">
              <div className={classes.upgradeButtonLabel}>
                <span>Mejora tu plan para acceder a esta funcionalidad</span>
                <UpgradeIcon width={18} height={18} />
              </div>
            </CustomButton>
          </Grid>
        )}
        <Grid item xs={12}>
          <section className={classes.sectionCard}>
            <header className={classes.sectionHeader}>
              <Typography className={classes.subtitle}>Tus bots</Typography>
              <Box
                display="flex"
                flexDirection="row"
                justifyContent="flex-end"
                alignItems="center"
              >
                {/* <Box mr={2}>
                  <SearchInput placeholder="Buscar" />
                </Box> */}

                {canCreateNewBot && (
                  <Box>
                    <CustomButton
                      variant="contained"
                      color="primary"
                      onClick={() => {
                        setOpenModal(true);
                      }}
                    >
                      Crear bot
                    </CustomButton>
                  </Box>
                )}
              </Box>
            </header>

            {chatBots.length === 0 && (
              <div className={classes.emptyContent}>
                <Typography color="textSecondary">
                  No tienes bots creados aún
                </Typography>
                <CustomButton
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    setOpenModal(true);
                  }}
                  disabled={!hasStoreAiChatBotFeature}
                >
                  Crear tu primer bot
                </CustomButton>
              </div>
            )}

            {chatBots.length > 0 && (
              <TableContainer>
                <Table aria-label="store bots tables">
                  <TableHead className={classes.tableHead}>
                    <TableRow>
                      <TableCell>Nombre</TableCell>
                      <TableCell align="left">Estado</TableCell>
                      <TableCell align="right">Acciones</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {chatBots.map(chatBot => {
                      const isTraining = chatBot.state === 'TRAINING';

                      if (chatBot.archived) {
                        return null;
                      }

                      return (
                        <TableRow key={chatBot.id} className={classes.tableRow}>
                          <TableCell>
                            <Box display="flex" flexDirection="column">
                              <Typography className={classes.botName}>
                                {chatBot.name}
                              </Typography>
                              {chatBot.enabled && !chatBot.archived && (
                                <Typography className={classes.activeText}>
                                  Activo
                                </Typography>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <CustomChip
                              label={getChatBotStateLabel(
                                chatBot.state,
                                chatBot.archived
                              )}
                              color={getChatBotStateColor(
                                chatBot.state,
                                chatBot.archived
                              )}
                              size="small"
                            />
                          </TableCell>
                          <TableCell align="right">
                            {chatBot.archived ? (
                              <CustomChip
                                label="Archivado"
                                color="danger"
                                size="small"
                              />
                            ) : (
                              <Box
                                display="flex"
                                flexDirection="row"
                                justifyContent="flex-end"
                                alignItems="center"
                              >
                                <Box mr={2}>
                                  <IosSwitch
                                    className={classes.switchContainer}
                                    checked={chatBot.enabled}
                                    onChange={() => {
                                      toggleEnableChatBot(
                                        chatBot.id,
                                        !chatBot.enabled
                                      ).then(success => {
                                        if (success) {
                                          refresh();
                                        }
                                      });
                                    }}
                                    disabled={isTraining}
                                    name="enabled"
                                  />
                                </Box>
                                {isTraining ? (
                                  <Box
                                    display="flex"
                                    justifyContent="center"
                                    alignItems="center"
                                    width={40}
                                  >
                                    <InlineLoader loading={true} size={20} />
                                  </Box>
                                ) : (
                                  <>
                                    <IconButton
                                      color="primary"
                                      size="small"
                                      component={Link}
                                      to={`/store-bot/chatbot-configuration?id=${chatBot.id}`}
                                    >
                                      <Settings />
                                    </IconButton>
                                    {/*                              <IconButton
                                      color="primary"
                                      size="small"
                                      component={Link}
                                      to={`/store-bot/training-bot/${chatBot.id}`}
                                    >
                                      <Edit />
                                    </IconButton> */}
                                    <IconButton
                                      className={classes.deleteButton}
                                      size="small"
                                      onClick={() => handleDeleteBot(chatBot)}
                                    >
                                      <Delete />
                                    </IconButton>
                                  </>
                                )}
                              </Box>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </section>
        </Grid>
      </Grid>

      <BotCreationModal
        open={openModal}
        isSubmitting={isLoading}
        onClose={() => setOpenModal(false)}
        onSubmit={handleCreateBot}
      />

      <ConfirmModal
        open={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        onCancel={() => setConfirmDeleteOpen(false)}
        onConfirm={confirmDelete}
        title="¿Estás seguro de eliminar este bot?"
        message="Esta acción es irreversible. Se eliminará permanentemente el bot, su configuración y todo el historial de entrenamiento asociado."
        confirmButtonLabel="Eliminar"
      />
    </div>
  );
}
